/**
 * Split document navigation from immortal SSE.
 *
 * Chrome coalesces every tab to one HTTP/2 connection per origin. When SSE and
 * GET / share that origin, a wedged session means the new tab never commits HTML
 * (Chrome tab spinner, white page, Stop returns to a blank URL).
 *
 * Point liveOrigin at a different host (HTTP/1.1, cert that does not also name
 * the document host) so GET / cannot share the SSE session.
 *
 * Keep in sync with `src/http2-new-tab-hang/join-hub-live-stream-url.ts`.
 */
export function joinHubLiveStreamUrl(liveOrigin: string | null | undefined, path: string, fallbackUrl: string): string {
  const origin = liveOrigin?.trim() ?? "";
  if (origin === "") {
    return fallbackUrl;
  }
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${origin.replace(/\/$/, "")}${normalized}`;
}

let runtimeLiveStreamOrigin: string | null | undefined;

function normalizeLiveOrigin(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  if (trimmed === "") {
    return null;
  }
  return trimmed.replace(/\/$/, "");
}

function viteLiveStreamOrigin(): string | null {
  const fromEnv = (import.meta.env?.VITE_HUB_LIVE_ORIGIN as string | undefined)?.trim() ?? "";
  return normalizeLiveOrigin(fromEnv);
}

export function setRuntimeHubLiveStreamOrigin(origin: string | null): void {
  runtimeLiveStreamOrigin = normalizeLiveOrigin(origin);
}

export function resolvedHubLiveStreamOrigin(): string | null {
  if (runtimeLiveStreamOrigin !== undefined) {
    return runtimeLiveStreamOrigin;
  }
  return viteLiveStreamOrigin();
}

/** Test-only reset. */
export function resetHubLiveStreamOriginForTests(): void {
  runtimeLiveStreamOrigin = undefined;
}
